import React from "react";
import {Redirect, Switch} from "react-router-dom";
import {GuardFunction, GuardProvider, GuardedRoute as Route, Next} from "react-router-guards";
import {Location} from "history";
import {observer} from "mobx-react";

import {store} from "@/stores";
import Subscription from "@/pages/Subscription/Subscription";
import Catalog from "@/pages/Catalog/Catalog";
import Ensemble from "@/pages/Ensemble/Ensemble";
import Ensembles from "@/pages/Ensembles/Ensembles";
import ErrorBoundary from "@/components/ErrorBoundary/ErrorBoundary";
import Home from "@/pages/Home/Home";
import InvitationAccepted from "@/pages/InvitationAccepted/InvitationAccepted";
import Loading from "@/components/Loading/Loading";
import NotFound from "@/pages/NotFound/NotFound";
import paths from "./paths";
import PasswordForgotten from "@/pages/PasswordForgotten/PasswordForgotten";
import Pieces from "@/pages/Pieces/Pieces";
import Rehearsals from "@/pages/Rehearsals/Rehearsals";
import SignIn from "@/pages/SignIn/SignIn";
import SignUp from "@/pages/SignUp/SignUp";
import SignUpInvitation from "@/pages/SignUpInvitation/SignUpInvitation";
import SignUpSuccess from "@/pages/SignUpSuccess/SignUpSuccess";
import SubscriptionProcessing from "@/pages/SubscriptionProcessing/SubscriptionProcessing";

// Lazy loaded components
// https://reactjs.org/docs/code-splitting.html#route-based-code-splitting
const EnsembleRehearsals = React.lazy(() => import("@/pages/EnsembleRehearsals/EnsembleRehearsals"));
const Piece = React.lazy(() => import("@/pages/Piece/Piece"));
const PieceNew = React.lazy(() => import("@/pages/PieceNew/PieceNew"));
const PieceNewVersion = React.lazy(() => import("@/pages/PieceNew/PieceNewVersion"));
const VirtualChoir = React.lazy(() => import("@/pages/VirtualChoir/VirtualChoir"));

export interface IRouteProps {}

// location.state object we use to send on redirects when want to keep the referrer
export interface ILocationStateReferrerProps {
  referrer?: Location;
}

const redirectWithReferrer = (pathname: string, next: Next, location?: Location) => {
  const state: ILocationStateReferrerProps = {referrer: location};
  next.redirect({pathname, state});
};

// selectedEnsemble is an active ensemble (because inactive ensembles cannot be selected)
const requireEnsemble: GuardFunction = (to, from, next) => {
  if (store.session.selectedEnsemble) next();
  next.redirect({pathname: paths.subscription()});
};

const requireConductor: GuardFunction = (to, from, next) => {
  if (store.session.isEnsembleConductor) next();
  redirectWithReferrer(paths.home(), next, from?.location);
};

// User has to be a conductor in the selectedEnsemble and
// the owner the selectedEnsemble has to be subscribed to Pro or Ensemble plan
const requireConductorOfProOrEnsemble: GuardFunction = (to, from, next) => {
  if (store.session.isConductorOfProOrEnsemble) next();
  redirectWithReferrer(paths.home(), next, from?.location);
};

// const requireOwner: GuardFunction = (to, from, next) => {
//   if (store.session.isEnsembleOwner) next();
//   redirectWithReferrer(paths.home(), next, from?.location);
// };

const requireUser: GuardFunction = (pathname, from, next) => {
  if (store.session.isSignedIn) next();
  redirectWithReferrer(paths.signIn(), next, from?.location);
};

// const requireSubscription: GuardFunction = (to, from, next) => {
//   if (store.session.isSubscribed) next();
//   redirectWithReferrer(paths.subscription(), next, from?.location);
// };

// const requireProOrEnsemblePlan: GuardFunction = (to, from, next) => {
//   if (store.session.hasPlanEnsemble || store.session.hasPlanPro) next();
//   redirectWithReferrer(paths.home(), next, from?.location);
// };

@observer
export default class Routes extends React.Component<IRouteProps> {
  render() {
    return (
      // TODO: use transitions for Suspense
      // https://reactjs.org/docs/concurrent-mode-patterns.html#transitions
      <React.Suspense fallback={<Loading />}>
        <ErrorBoundary>
          <GuardProvider loading={Loading} error={NotFound}>
            <Switch>
              <Redirect exact from={paths.root()} to={paths.home()} />
              <Route exact path={paths.signIn()} component={SignIn} />
              <Route exact path={paths.signUp()} component={SignUp} />
              <Route exact path={paths.signUpInvitation(":userInvitationId")} component={SignUpInvitation} />
              <Route exact path={paths.signUpSuccess()} component={SignUpSuccess} />
              <Route exact path={paths.invitationAccepted()} component={InvitationAccepted} />
              <Route exact path={paths.passwordForgotten()} component={PasswordForgotten} />
              <Route exact path={paths.subscription()} component={Subscription} guards={[requireUser]} />
              <Route
                exact
                path={paths.subscriptionProcessing()}
                component={SubscriptionProcessing}
                guards={[requireUser]}
              />
              <Route exact path={paths.home()} guards={[requireUser, requireEnsemble]}>
                <Home />
              </Route>
              <Route
                exact
                path={paths.catalog()}
                component={Catalog}
                guards={[requireUser, requireEnsemble, requireConductor]}
              />
              <Route
                exact
                path={paths.selectedEnsemble()}
                component={Ensemble}
                guards={[requireUser, requireEnsemble, requireConductor]}
              />
              <Route exact path={paths.ensembles()} component={Ensembles} guards={[requireUser]} />
              <Route exact path={paths.pieces()} component={Pieces} guards={[requireUser, requireEnsemble]} />
              <Route
                exact
                path={paths.pieceNew()}
                component={PieceNew}
                guards={[requireUser, requireEnsemble, requireConductorOfProOrEnsemble]}
              />
              <Route
                exact
                path={paths.pieceNewVersion(":pieceId")}
                component={PieceNewVersion}
                guards={[requireUser, requireEnsemble, requireConductorOfProOrEnsemble]}
              />
              <Route exact path={paths.piece(":pieceId")} component={Piece} guards={[requireUser, requireEnsemble]} />
              <Route exact path={paths.rehearsals()} component={Rehearsals} guards={[requireUser, requireEnsemble]} />
              <Route
                exact
                path={paths.ensembleRehearsals()}
                component={EnsembleRehearsals}
                guards={[requireUser, requireEnsemble, requireConductorOfProOrEnsemble]}
              />
              <Route
                exact
                path={paths.virtualChoir(":pieceId")}
                component={VirtualChoir}
                guards={[requireUser, requireEnsemble, requireConductor]}
              />
              {/* If no route matches show Not Found page */}
              <Route path="*" component={NotFound} />
            </Switch>
          </GuardProvider>
        </ErrorBoundary>
      </React.Suspense>
    );
  }
}
